import {
	Component, OnInit, OnDestroy, ViewChild, ViewChildren, QueryList, ChangeDetectorRef
} from '@angular/core';
import { Subscription } from 'rxjs/Rx';
import { IdentityService, UserPermissionsService } from 'identity';
import { NavigationService } from 'netview';
import { MessageService } from 'base';
import { UserListComponent, GroupListComponent } from 'identity';
import { AccountType } from 'UvExplorerServer.Dtos.TS';
import { AuthSettingsComponent } from 'identity';
import { ConfigurationService } from '../../../configuration';
import { Integration } from '../../../DiscoveryEngine.TS/Core/Integration';


@Component({
    selector: 'tools',
	templateUrl: './tools.component.html',
    styleUrls: ['./tools.component.scss']
})
export class ToolsComponent implements OnInit, OnDestroy {

	@ViewChildren(UserListComponent)
	public userLists: QueryList<UserListComponent>;

	@ViewChildren(GroupListComponent)
	public groupLists: QueryList<GroupListComponent>;

	@ViewChildren(AuthSettingsComponent)
	public authSettings: QueryList<AuthSettingsComponent>;

	public activeIndex: number;

	private get integrationTable() { return this.configService.integrationTable; }
	private onIntegrationSettingsLoadedSub: Subscription;

	constructor(private identityService: IdentityService,
		private userPermissionsService: UserPermissionsService,
		private configService: ConfigurationService,
		private messageService: MessageService,
		private navService: NavigationService,
		private changeDetector: ChangeDetectorRef) {

		if (!this.hasIntegrationTable) {
			this.configService.getIntegrationTable();
			this.onIntegrationSettingsLoadedSub = this.configService.onIntegrationTableLoaded.subscribe(
				() => {
					this.changeDetector.detectChanges();
				},
				() => {
					console.error("Could not get the integration table");
					this.messageService.displayErrorMessage("Failed to retrieve integration table.");
				});
		}

		this.activeIndex = 0;
	}

	public ngOnInit(): void {
	}

	public ngAfterViewInit() {
		if (this.navService.location.hasContext) {
			const UserListCreateUserGoogleWorkspacePath = '/userlist/createuser/googleworkspace';
			const UserListEditUserGoogleWorkspacePath = '/userlist/edituser/googleworkspace/';
			const UserListLoginAdminFailedPath = '/userlist/loginadmin/failed';
			const GroupListCreateGroupGoogleWorkspacePath = '/grouplist/creategroup/googleworkspace';
			const GroupListEditGroupGoogleWorkspacePath = '/grouplist/editgroup/googleworkspace/';
			const GroupListSyncGroupsGoogleWorkspacePath = '/grouplist/syncgroups/googleworkspace';
			const GroupListLoginAdminFailedPath = '/grouplist/loginadmin/failed';
			const AuthSettingsSaveGoogleWorkspacePath = '/authsettings/save/googleworkspace';
			const AuthSettingsLoginAdminFailedPath = '/authsettings/loginadmin/failed';

			const UserListTabIndex = 2;
			const GroupListTabIndex = 3;
			const AuthSettingsTabIndex = 4;

			let context = this.navService.location.context;
			if (context === UserListCreateUserGoogleWorkspacePath) {
				setTimeout(() => {
					this.setSelectedTab(UserListTabIndex);
					let userlist = this.userLists.first;
					setTimeout(() => {
						userlist.createDirectoryUser(AccountType.GoogleWorkspace);
					}, 0);
				}, 0);
			}
			else if (context.startsWith(UserListEditUserGoogleWorkspacePath)) {
				let userGuid = context.substr(UserListEditUserGoogleWorkspacePath.length);
				if (userGuid) {
					setTimeout(() => {
						this.setSelectedTab(UserListTabIndex);
						let userlist = this.userLists.first;
						setTimeout(() => {
							userlist.editDirectoryUserByGuid(userGuid);
						}, 0);
					}, 0);
				}
			}
			else if (context === UserListLoginAdminFailedPath) {
				setTimeout(() => {
					this.setSelectedTab(UserListTabIndex);
					let userlist = this.userLists.first;
					setTimeout(() => {
						userlist.onAdminLoginFailed();
					}, 0);
				}, 0);
			}
			else if (context === GroupListCreateGroupGoogleWorkspacePath) {
				setTimeout(() => {
					this.setSelectedTab(GroupListTabIndex);
					let grouplist = this.groupLists.first;
					setTimeout(() => {
						grouplist.createDirectoryGroup(AccountType.GoogleWorkspace);
					}, 0);
				}, 0);
			}
			else if (context.startsWith(GroupListEditGroupGoogleWorkspacePath)) {
				let groupGuid = context.substr(GroupListEditGroupGoogleWorkspacePath.length);
				if (groupGuid) {
					setTimeout(() => {
						this.setSelectedTab(GroupListTabIndex);
						let grouplist = this.groupLists.first;
						setTimeout(() => {
							grouplist.editDirectoryGroupByGuid(groupGuid);
						}, 0);
					}, 0);
				}
			}
			else if (context === GroupListSyncGroupsGoogleWorkspacePath) {
				setTimeout(() => {
					this.setSelectedTab(GroupListTabIndex);
					let grouplist = this.groupLists.first;
					setTimeout(() => {
						grouplist.syncDirectoryGroups(AccountType.GoogleWorkspace);
					}, 0);
				}, 0);
			}
			else if (context === GroupListLoginAdminFailedPath) {
				setTimeout(() => {
					this.setSelectedTab(GroupListTabIndex);
					let grouplist = this.groupLists.first;
					setTimeout(() => {
						grouplist.onAdminLoginFailed();
					}, 0);
				}, 0);
			}
			else if (context === AuthSettingsSaveGoogleWorkspacePath) {
				setTimeout(() => {
					this.setSelectedTab(AuthSettingsTabIndex);
					let authSettings = this.authSettings.first;
					setTimeout(() => {
						authSettings.onOauthSave();
					}, 0);
				}, 0);
			}
			else if (context === AuthSettingsLoginAdminFailedPath) {
				setTimeout(() => {
					this.setSelectedTab(AuthSettingsTabIndex);
					let authSettings = this.authSettings.first;
					setTimeout(() => {
						authSettings.onAdminLoginFailed();
					}, 0);
				}, 0);
			}
		}
	}

	private setSelectedTab(tabIndex: number): void {
		this.activeIndex = tabIndex;
	}

	public ngOnDestroy(): void {
		if (this.onIntegrationSettingsLoadedSub) {
			this.onIntegrationSettingsLoadedSub.unsubscribe();
			this.onIntegrationSettingsLoadedSub = undefined;
		}
	}

	public get hasIntegrationTable(): boolean {
		return (!!this.integrationTable);
	}

	public get isLoggedIn(): boolean {
		return this.identityService.isLoggedIn;
	}

	public get isAdmin(): boolean {
		return this.identityService.isAdmin;
	}

	public get isObserver(): boolean {
		return this.identityService.isObserver;
	}

	public get canEnableDisablePorts(): boolean {
		return this.userPermissionsService.canEnableDisablePorts;
	}

	public get canManagePortPoe(): boolean {
		return this.userPermissionsService.canManagePortPoe;
	}

	public get canManagePortAccessVlans(): boolean {
		return this.userPermissionsService.canManagePortAccessVlans;
	}

	public get canViewJackLookup(): boolean {
		return this.userPermissionsService.canViewJackLookup;
	}

}
